// NEWS2 obs-chart panel for pre-arrest scenarios. Auto-calculates the National
// Early Warning Score 2 from the patient's current vitals and shows the breakdown
// + total + risk band. RCUK trigger thresholds (A-19.md): ≥7 = critical-care review,
// ≥5 (or any single parameter scoring 3) = urgent review.
//
// Score parameter thresholds (NEWS2 2017):
//   Respiratory rate ......... ≤8 (3), 9-11 (1), 12-20 (0), 21-24 (2), ≥25 (3)
//   SpO2 ..................... ≤91 (3), 92-93 (2), 94-95 (1), ≥96 (0)
//   Air/Oxygen ............... room air (0), supplemental (2)
//   Systolic BP .............. ≤90 (3), 91-100 (2), 101-110 (1), 111-219 (0), ≥220 (3)
//   Pulse rate ............... ≤40 (3), 41-50 (1), 51-90 (0), 91-110 (1), 111-130 (2), ≥131 (3)
//   Consciousness (ACVPU) .... Alert (0), New confusion/V/P/U (3)
//   Temperature .............. ≤35.0 (3), 35.1-36.0 (1), 36.1-38.0 (0), 38.1-39.0 (1), ≥39.1 (2)

interface Props {
  hr: number;
  sbp: number;
  spo2: number;
  rr: number;
  gcs: number;     // we use GCS as a proxy for ACVPU — <15 = "new confusion" (3 points)
  temp_c?: number; // optional; if absent, assume 36.5 (normal)
  on_oxygen?: boolean;
}

function scoreRR(rr: number): number {
  if (rr <= 8) return 3;
  if (rr <= 11) return 1;
  if (rr <= 20) return 0;
  if (rr <= 24) return 2;
  return 3;
}
function scoreSpO2(spo2: number): number {
  if (spo2 <= 91) return 3;
  if (spo2 <= 93) return 2;
  if (spo2 <= 95) return 1;
  return 0;
}
function scoreSBP(sbp: number): number {
  if (sbp <= 90) return 3;
  if (sbp <= 100) return 2;
  if (sbp <= 110) return 1;
  if (sbp <= 219) return 0;
  return 3;
}
function scoreHR(hr: number): number {
  if (hr <= 40) return 3;
  if (hr <= 50) return 1;
  if (hr <= 90) return 0;
  if (hr <= 110) return 1;
  if (hr <= 130) return 2;
  return 3;
}
function scoreTemp(t: number): number {
  if (t <= 35.0) return 3;
  if (t <= 36.0) return 1;
  if (t <= 38.0) return 0;
  if (t <= 39.0) return 1;
  return 2;
}
function scoreGCS(gcs: number): number { return gcs < 15 ? 3 : 0; }

function bandColour(total: number): string {
  if (total >= 7) return 'text-alarm-red';
  if (total >= 5) return 'text-alarm-amber';
  return 'text-mono-green';
}
function bandLabel(total: number): string {
  if (total >= 7) return 'HIGH — urgent critical-care review';
  if (total >= 5) return 'MEDIUM — urgent ward review';
  return 'LOW — routine monitoring';
}

export default function NEWS2Panel({ hr, sbp, spo2, rr, gcs, temp_c = 36.5, on_oxygen = false }: Props) {
  const rows = [
    { label: 'Respiratory rate',    value: `${rr}/min`,                    score: scoreRR(rr) },
    { label: 'SpO₂',                value: `${spo2}%`,                     score: scoreSpO2(spo2) },
    { label: 'Air / Oxygen',        value: on_oxygen ? 'Supplemental' : 'Room air', score: on_oxygen ? 2 : 0 },
    { label: 'Systolic BP',         value: `${sbp} mmHg`,                  score: scoreSBP(sbp) },
    { label: 'Pulse',               value: `${hr}/min`,                    score: scoreHR(hr) },
    { label: 'Consciousness (ACVPU)', value: gcs < 15 ? `GCS ${gcs} (alt)` : 'Alert', score: scoreGCS(gcs) },
    { label: 'Temperature',         value: `${temp_c.toFixed(1)}°C`,       score: scoreTemp(temp_c) },
  ];
  const total = rows.reduce((s, r) => s + r.score, 0);
  const anyThree = rows.some((r) => r.score === 3);

  return (
    <div className="panel p-3 select-none">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-mono-white/60 text-xs uppercase tracking-widest">NEWS2</h3>
        <div className="flex items-center gap-2">
          <span className={`text-2xl font-mono tabular-nums font-bold ${bandColour(total)}`}>{total}</span>
          <span className={`text-[10px] uppercase tracking-widest ${bandColour(total)}`}>{bandLabel(total)}</span>
        </div>
      </div>
      <table className="w-full text-xs tabular-nums">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-ink-800 last:border-0">
              <td className="text-mono-white/60 py-1">{r.label}</td>
              <td className="text-mono-white py-1 text-right">{r.value}</td>
              <td className={`py-1 pl-3 text-right font-mono w-12 ${r.score === 0 ? 'text-mono-white/30' : r.score === 1 ? 'text-mono-white/70' : r.score === 2 ? 'text-alarm-amber' : 'text-alarm-red'}`}>
                +{r.score}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {anyThree && total < 5 && (
        <div className="mt-2 text-[10px] text-alarm-amber uppercase tracking-widest">
          ⚠ Single parameter scoring 3 — escalate regardless of total
        </div>
      )}
    </div>
  );
}
